import { formatDateForInput, parseISO } from "@/shared/lib/date-time";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/shared/ui/DatePicker";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDebounceCallback } from "@/shared/hooks/use-debounce-callback";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { ChevronDownIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";

const LEDGER_TYPES = [
  { value: "TOPUP", label: "TOPUP" },
  { value: "PAYMENT", label: "PAYMENT" },
  { value: "REFUND", label: "REFUND" },
  { value: "ADJUSTMENT", label: "ADJUSTMENT" },
] as const;

type LedgerSearch = {
  walletId?: string;
  search?: string;
  createdFrom?: string;
  createdTo?: string;
  type?: string;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  offset?: number;
  limit?: number;
};

function parseTypeSet(typeStr?: string): Set<string> {
  if (!typeStr?.trim()) return new Set();
  return new Set(typeStr.split(",").map((s) => s.trim().toUpperCase()));
}

export function LedgerFilter() {
  const nav = useNavigate({ from: "/app/wallet/ledger" });
  const search = useSearch({ from: "/app/wallet/ledger" }) as LedgerSearch;

  const hasFilter =
    !!search.walletId ||
    !!search.search ||
    !!search.createdFrom ||
    !!search.createdTo ||
    !!search.type;

  const [searchInputValue, setSearchInputValue] = useState<string>(
    search.search ?? "",
  );
  useEffect(() => {
    setSearchInputValue(search.search ?? "");
  }, [search.search]);

  const debouncedSearch = useDebounceCallback((val: string) => {
    setSearchInputValue(val);
    update({ search: val || undefined });
  }, 400);

  const typeSet = parseTypeSet(search.type);

  const update = (next: Partial<LedgerSearch>) => {
    nav({ search: { ...search, ...next, offset: 0 } });
  };

  const toggleType = (value: string) => {
    const next = new Set(typeSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    const typeStr = next.size ? Array.from(next).sort().join(",") : undefined;
    update({ type: typeStr });
  };

  const clearFilters = () => {
    setSearchInputValue("");
    nav({
      search: {
        offset: search.offset ?? 0,
        limit: search.limit ?? 20,
        sortField: search.sortField,
        sortOrder: search.sortOrder,
      },
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-4 pb-2">
      {search.walletId && (
        <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-muted-foreground text-sm">
          ບັນຊີ: {search.walletId.slice(0, 8)}…
          <button
            type="button"
            onClick={() => update({ walletId: undefined })}
            className="hover:text-foreground"
            aria-label="ລຶບ"
          >
            <XIcon className="size-3" />
          </button>
        </span>
      )}
      <DatePicker
        mode="range"
        value={{
          from: search.createdFrom ? parseISO(search.createdFrom) : undefined,
          to: search.createdTo ? parseISO(search.createdTo) : undefined,
        }}
        onChange={(range) => {
          const from = Array.isArray(range)
            ? range[0]
            : (range as { from?: Date; to?: Date })?.from;
          const to = Array.isArray(range)
            ? range[1]
            : (range as { from?: Date; to?: Date })?.to;
          update({
            createdFrom: from ? formatDateForInput(from) : undefined,
            createdTo: to ? formatDateForInput(to) : undefined,
          });
        }}
        placeholder="ຊ່ວງວັນທີ"
        className="h-9 w-fit min-w-56"
      />
      <Input
        type="search"
        placeholder="ຄົ້ນຫາລູກຄ້າ (ຊື່ / ເບີ)"
        className="w-56"
        defaultValue={searchInputValue}
        onChange={(e) => debouncedSearch(e.target.value)}
      />
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 gap-1">
            ປະເພດ
            {typeSet.size > 0 && (
              <span className="rounded bg-primary/10 px-1.5 text-primary text-xs">
                {typeSet.size}
              </span>
            )}
            <ChevronDownIcon className="size-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-48 p-2">
          <fieldset className="flex flex-col gap-1 border-0 p-0">
            <legend className="sr-only">ປະເພດທຸລະກຳ</legend>
            {LEDGER_TYPES.map(({ value, label }) => {
              const id = `ledger-type-${value}`;
              return (
                <label
                  key={value}
                  htmlFor={id}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-muted/50"
                >
                  <Checkbox
                    id={id}
                    checked={typeSet.has(value)}
                    onCheckedChange={() => toggleType(value)}
                  />
                  <span className="text-sm">{label}</span>
                </label>
              );
            })}
          </fieldset>
        </PopoverContent>
      </Popover>
      {hasFilter && (
        <button
          type="button"
          onClick={clearFilters}
          className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <XIcon className="size-4" />
          ລ້າງຕົວກອງ
        </button>
      )}
    </div>
  );
}
